import { salesOrderRepFieldsLockedIn } from "./salesOrderRepLock.js";

export interface SalesOrderEditorSavedRepFields {
  status?: string | null;
  repAgencyId?: string | null;
  repAgencyName?: string | null;
  repStaffId?: string | null;
  repStaffName?: string | null;
  repSyncAt?: string | null;
  salesManagerName?: string | null;
  repSplits?: Array<{ name: string; shareNumerator: number; shareDenominator: number }>;
}

export interface SalesOrderEditorDraftRepFields {
  repAgencyName?: string | null;
  repStaffName?: string | null;
  salesManagerName?: string | null;
}

export function formatSalesOrderRepFieldDisplay(
  lockedValue: string | null | undefined,
  draftValue: string | null | undefined,
  repFieldsLockedIn: boolean,
): string {
  if (repFieldsLockedIn) {
    return lockedValue?.trim() ?? "";
  }
  const draft = draftValue?.trim();
  return draft ? `(draft) ${draft}` : "";
}

export function formatSalesOrderRepFieldLabel(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ?? "";
}

export function formatSalesOrderRepSplitDisplay(
  rows: Array<{ name: string; shareNumerator: number; shareDenominator: number }>,
): string | null {
  if (rows.length < 2) {
    return null;
  }
  return rows
    .map((row) => {
      const denominator = row.shareDenominator > 0 ? row.shareDenominator : 1;
      const percent = Math.round((100 * row.shareNumerator) / denominator);
      return `${row.name} ${percent}%`;
    })
    .join(" / ");
}

export function resolveSalesOrderEditorRepFieldsLockedIn(args: {
  savedOrder: SalesOrderEditorSavedRepFields | null | undefined;
  /** Local form status before the detail payload hydrates. */
  fallbackStatus?: string | null;
}): boolean {
  const savedOrder = args.savedOrder;
  const status =
    savedOrder?.status != null && String(savedOrder.status).trim() !== ""
      ? savedOrder.status
      : args.fallbackStatus;
  return salesOrderRepFieldsLockedIn({
    status,
    repAgencyId: savedOrder?.repAgencyId,
    repSyncAt: savedOrder?.repSyncAt,
  });
}

export function buildSalesOrderEditorRepDisplays(args: {
  savedOrder: SalesOrderEditorSavedRepFields | null | undefined;
  draftEndUser: SalesOrderEditorDraftRepFields | null | undefined;
  fallbackStatus?: string | null;
}): {
  repFieldsLockedIn: boolean;
  salesManagerDisplay: string;
  repAgencyDisplay: string;
  repStaffDisplay: string;
  repFieldTitle: string | undefined;
} {
  const repFieldsLockedIn = resolveSalesOrderEditorRepFieldsLockedIn({
    savedOrder: args.savedOrder,
    fallbackStatus: args.fallbackStatus,
  });
  const savedOrder = args.savedOrder;
  const draftEndUser = args.draftEndUser;

  const lockedRepAgencyDisplay =
    formatSalesOrderRepSplitDisplay(savedOrder?.repSplits ?? []) ??
    savedOrder?.repAgencyName?.trim() ??
    "";

  const salesManagerDisplay = formatSalesOrderRepFieldDisplay(
    repFieldsLockedIn ? (savedOrder?.salesManagerName ?? null) : null,
    draftEndUser?.salesManagerName,
    repFieldsLockedIn,
  );
  const repAgencyDisplay = repFieldsLockedIn
    ? lockedRepAgencyDisplay
    : formatSalesOrderRepFieldDisplay(null, draftEndUser?.repAgencyName, false);
  const repStaffDisplay = formatSalesOrderRepFieldDisplay(
    repFieldsLockedIn ? (savedOrder?.repStaffName ?? null) : null,
    draftEndUser?.repStaffName,
    repFieldsLockedIn,
  );

  return {
    repFieldsLockedIn,
    salesManagerDisplay,
    repAgencyDisplay,
    repStaffDisplay,
    repFieldTitle: repFieldsLockedIn
      ? undefined
      : "Preview from the selected end user. These values are saved when the order is approved.",
  };
}
